import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { seoArticles } from '@/mocks/articles';
import { kaspreadsheetUrl } from '@/mocks/categories';

declare const __SITE_URL__: string;

export default function SeoArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (!article) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      url: `${__SITE_URL__}/shopping-guide/${article.slug}`,
      datePublished: article.date,
      dateModified: article.date,
      author: { '@type': 'Organization', 'name': 'gtbuyspreadsheet.team' },
    });
    document.head.appendChild(script);

    return () => {
      if (script && script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <main className="pt-20">
          <section className="py-20 px-4 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <Link to="/shopping-guide" className="text-primary-500 hover:text-primary-600 font-semibold text-sm">
              Back to Shopping Guide
            </Link>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <main className="pt-16">
        {/* Article Header */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              <div className="lg:w-2/5">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                  {article.date}
                </span>
                <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.imageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:w-3/5">
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6 max-w-lg">
                  {article.summary}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/shopping-guide"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-foreground-200 text-foreground-700 text-sm font-semibold hover:border-foreground-400 hover:text-foreground-950 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                    All Guides
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-foreground-200 text-foreground-700 text-sm font-semibold hover:border-foreground-400 hover:text-foreground-950 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                    Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Quick Summary */}
            <div className="bg-background-100 rounded-xl p-6 mb-12 border border-background-200/70">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">What You Will Learn</h2>
              <ul className="space-y-2">
                {article.sections.map((section, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-accent-500 font-semibold text-sm mt-0.5">{idx + 1}.</span>
                    <a href={`#section-${idx}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sections */}
            {article.sections.map((section, idx) => (
              <section key={idx} id={`section-${idx}`} className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.title}
                </h2>
                <div className="text-foreground-700 text-sm leading-relaxed space-y-4">
                  {section.content.split('\n').filter(Boolean).map((paragraph, pIdx) => (
                    <p key={pIdx}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}

            {/* Bottom Navigation */}
            <div className="border-t border-background-200/70 pt-10 mt-10">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <p className="text-xs text-foreground-500 mb-2">Explore More Guides</p>
                  <Link
                    to="/shopping-guide"
                    className="inline-flex items-center gap-2 text-sm text-primary-500 font-semibold hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-book-open-line w-4 h-4 flex items-center justify-center"></i>
                    View All Shopping Guides
                  </Link>
                </div>
                <a
                  href={kaspreadsheetUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  Browse Full Catalog
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>

            {/* Related */}
            <div className="mt-10 p-6 rounded-xl bg-background-100">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">Related Resources</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/gtbuy-spreadsheet-complete-guide" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                    GTBuy Spreadsheet Explained: The Ultimate 2026 Setup Guide
                  </Link>
                </li>
                <li>
                  <Link to="/shopping-guide" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                    Complete Shopping Guide &mdash; All Articles
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                    Home &mdash; Compare Spreadsheet Systems
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}